// Certificado de reproducción PÚBLICO por folio (/signage/cert/:folio — fuera del
// login, como /signage/tv/:token). La agencia/anunciante escanea el QR o abre el
// enlace y ve el snapshot INMUTABLE de lo emitido (reproducciones, segundos,
// pantallas, % de cumplimiento), con verificación de integridad (hash). La misma
// página es la vista imprimible (window.print).
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { BadgeCheck, Printer, ShieldAlert } from 'lucide-react';
import { apiClient } from '@/lib/api/client';
import { LOCALE } from '@/lib/utils/regional';

interface CertMetricas {
  contrato?: { nombre?: string; anunciante?: string | null; ambito?: string; fecha_inicio?: string; fecha_fin?: string; tarifa?: number; unidad?: string; estado?: string };
  empresa?: string | null;
  resumen?: { impresiones?: number; segundos?: number; pantallas_contratadas?: number; pantallas_con_emision?: number; dias_periodo?: number; dias_con_emision?: number; cumplimiento_pct?: number };
  por_pantalla?: { pantalla: string; n: number; seg: number; dias: number }[];
  por_dia?: { dia: string; n: number }[];
}
interface Cert { valido: boolean; folio?: string; hash?: string; desde?: string; hasta?: string; emitido_en?: string; integro?: boolean; metricas?: CertMetricas }

const fmtSeg = (s: number) => { if (s < 60) return `${s}s`; const m = Math.round(s / 60); if (m < 60) return `${m} min`; const h = Math.floor(m / 60); return `${h}h ${m % 60}m`; };
const num = (n?: number) => Number(n ?? 0).toLocaleString(LOCALE);

export function CertificadoSignage() {
  const { folio } = useParams<{ folio: string }>();
  const { data, isLoading } = useQuery<Cert>({
    queryKey: ['sig-cert', folio], enabled: !!folio, retry: false,
    queryFn: async () => (await apiClient.post<Cert>('/rpc/sig_certificado_verificar', { _folio: folio })).data,
  });

  const maxDia = useMemo(() => Math.max(1, ...(data?.metricas?.por_dia ?? []).map((d) => d.n)), [data]);
  const url = typeof window !== 'undefined' ? window.location.href : '';

  if (isLoading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Verificando certificado…</div>;
  if (!data?.valido) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-slate-50 text-slate-700 p-8 text-center">
        <ShieldAlert className="w-14 h-14 text-red-500" />
        <h1 className="text-xl font-semibold">Certificado no encontrado</h1>
        <p className="text-sm max-w-md">El folio «{folio}» no corresponde a ningún certificado de reproducción emitido. Verifica el enlace o solicita uno nuevo al medio.</p>
      </div>
    );
  }

  const m = data.metricas ?? {};
  const r = m.resumen ?? {};
  const cumplimiento = Number(r.cumplimiento_pct ?? 0);

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4 print:bg-white print:py-0">
      <style>{`@media print { .no-print { display:none !important } .cert-hoja { box-shadow:none !important; border:none !important } }`}</style>
      <div className="cert-hoja mx-auto max-w-3xl bg-white text-slate-900 rounded-xl shadow-xl border p-10 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[.25em] text-slate-500">Certificado de reproducción</div>
            <h1 className="text-2xl font-bold mt-1">{m.empresa}</h1>
            <div className="text-sm text-slate-600">Cartelería digital — proof-of-play auditado</div>
          </div>
          <div className="text-center shrink-0">
            <QRCodeSVG value={url} size={92} level="M" />
            <div className="text-[10px] text-slate-500 mt-1">Verificación en línea</div>
          </div>
        </div>

        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          <BadgeCheck className="w-5 h-5 shrink-0" />
          <span>
            Folio <b className="font-mono">{data.folio}</b> · emitido el {new Date(data.emitido_en!).toLocaleString(LOCALE)} ·
            integridad {data.integro ? 'verificada' : '⚠ NO VERIFICADA'} (hash <span className="font-mono">{data.hash?.slice(0, 12)}…</span>)
          </span>
        </div>

        <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
          <div className="flex justify-between border-b py-1"><span className="text-slate-500">Anunciante</span><b>{m.contrato?.anunciante ?? '—'}</b></div>
          <div className="flex justify-between border-b py-1"><span className="text-slate-500">Contrato</span><b>{m.contrato?.nombre ?? '—'}</b></div>
          <div className="flex justify-between border-b py-1"><span className="text-slate-500">Período certificado</span><b>{data.desde} → {data.hasta}</b></div>
          <div className="flex justify-between border-b py-1"><span className="text-slate-500">Vigencia del contrato</span><b>{m.contrato?.fecha_inicio} → {m.contrato?.fecha_fin}</b></div>
        </div>

        <div className="grid grid-cols-4 gap-3 text-center">
          {[
            { v: num(r.impresiones), l: 'Reproducciones' },
            { v: fmtSeg(Number(r.segundos ?? 0)), l: 'Tiempo emitido' },
            { v: `${num(r.pantallas_con_emision)}/${num(r.pantallas_contratadas)}`, l: 'Pantallas con emisión' },
            { v: `${cumplimiento}%`, l: `Cumplimiento (${num(r.dias_con_emision)}/${num(r.dias_periodo)} días)` },
          ].map((k) => (
            <div key={k.l} className="rounded-lg border p-3">
              <div className="text-xl font-bold tabular-nums">{k.v}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{k.l}</div>
            </div>
          ))}
        </div>

        {(m.por_pantalla ?? []).length > 0 && (
          <div>
            <h2 className="text-sm font-semibold mb-1.5">Detalle por pantalla</h2>
            <table className="w-full text-sm">
              <thead><tr className="text-slate-500 border-b text-xs">
                <th className="py-1 text-left">Pantalla</th><th className="py-1 text-right">Reproducciones</th>
                <th className="py-1 text-right">Tiempo</th><th className="py-1 text-right">Días con emisión</th>
              </tr></thead>
              <tbody>
                {(m.por_pantalla ?? []).map((p) => (
                  <tr key={p.pantalla} className="border-b last:border-0">
                    <td className="py-1">{p.pantalla}</td>
                    <td className="py-1 text-right tabular-nums">{num(p.n)}</td>
                    <td className="py-1 text-right tabular-nums">{fmtSeg(Number(p.seg))}</td>
                    <td className="py-1 text-right tabular-nums">{num(p.dias)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(m.por_dia ?? []).length > 0 && (
          <div>
            <h2 className="text-sm font-semibold mb-1.5">Reproducciones por día</h2>
            <div className="flex items-end gap-1 h-20">
              {(m.por_dia ?? []).map((d) => (
                <div key={d.dia} className="flex-1 flex flex-col items-center gap-0.5" title={`${d.dia}: ${d.n}`}>
                  <div className="w-full bg-slate-700/70 rounded-t" style={{ height: `${(d.n / maxDia) * 100}%`, minHeight: d.n ? '2px' : 0 }} />
                  <span className="text-[9px] text-slate-500">{d.dia.slice(8, 10)}/{d.dia.slice(5, 7)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-[11px] leading-relaxed text-slate-500 border-t pt-3">
          Este certificado es un snapshot inmutable del registro de reproducciones (proof-of-play) generado por los propios
          reproductores al momento de emitir cada escena, congelado en la fecha de emisión del folio. La autenticidad puede
          verificarse en cualquier momento escaneando el QR o abriendo esta misma URL; el hash protege el contenido contra
          alteraciones. Documento generado por el módulo de Cartelería Digital.
        </p>

        <div className="no-print flex justify-end">
          <button className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-slate-50" onClick={() => window.print()}>
            <Printer className="w-4 h-4" /> Imprimir / PDF
          </button>
        </div>
      </div>
    </div>
  );
}
